import {
  Box,
  Flex,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { NavigationArticle } from '@bridge/navigationStore';
import { tagsApi } from '@texas/api/endpoints/tagsApi';
import { EntityType } from '@texas/api/endpoints/trackerApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { Icons } from '@texas/components/shared/Icons';
import { EditTagLinksModal } from '@texas/components/tags/EditTagLinksModal';
import { tagEvents } from '@texas/components/tags/events';
import { KeyboardEvent, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface ArticleContextMetadataProps {
  article: NavigationArticle;
}

export function ArticleContextMetadata({
  article,
}: ArticleContextMetadataProps) {
  const { t } = useTranslation();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { data, refetch } = useApiResource(tagsApi.getArticleTags);

  const refetchTags = useCallback(
    () =>
      refetch({
        articleIds: [article.id],
        includeSystemTags: false,
      }),
    [article.id, refetch],
  );

  useEffect(() => {
    void refetchTags();
  }, [refetchTags]);

  useEffect(() => {
    const subscriptions = [
      tagEvents.tagUpdated.sub(() => void refetchTags()),
      tagEvents.tagLinksUpdated.subscribe((event) => {
        if (
          event.entityType === EntityType.Article &&
          event.entityId === article.id
        ) {
          void refetchTags();
        }
      }),
    ];

    return () => subscriptions.forEach((unsubscribe) => unsubscribe());
  }, [article.id, refetchTags]);

  const tags = data?.find((item) => item.articleId === article.id)?.tags ?? [];
  const openOnEnterOrSpace = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen();
    }
  };

  return (
    <>
      {(article.hasMatrix || article.isVdc || article.collectionItem) && (
        <Flex align="center" flexWrap="wrap" gap={1.5} pt={1.5}>
          {article.hasMatrix && (
            <ArticleTypeBadge
              icon={<Icons.Grid />}
              label={t('general.matrix')}
            />
          )}
          {article.isVdc && (
            <ArticleTypeBadge
              filled={true}
              icon={<Icons.FormatAlignLeft />}
              label={t('navigation.vdc')}
            />
          )}
          {article.collectionItem && (
            <ArticleTypeBadge
              icon={<Icons.Layers />}
              label={t('article.management.collectionItem')}
            />
          )}
        </Flex>
      )}

      <Flex direction="column" gap={1.25} pt={1.5} role="group">
        <Flex align="center" h="20px" justify="space-between">
          <Text
            color="navigation.contextMuted"
            fontFamily="barlow"
            fontSize="11px"
            fontWeight="bold"
            letterSpacing="0.12em"
            lineHeight="14px"
          >
            {t('navigation.articleTags').toUpperCase()}
          </Text>
          <Tooltip label={t('article.setTags')}>
            <IconButton
              aria-label={t('article.setTags')}
              boxSize="20px"
              icon={<Icons.Pencil boxSize={3} />}
              minW="20px"
              opacity={0}
              size="xs"
              variant="ghost"
              _focusVisible={{ opacity: 1 }}
              _groupHover={{ opacity: 1 }}
              onClick={onOpen}
            />
          </Tooltip>
        </Flex>
        <Flex
          aria-label={t('article.setTags')}
          cursor="pointer"
          flexWrap="wrap"
          gap={1}
          role="button"
          tabIndex={0}
          onClick={onOpen}
          onKeyDown={openOnEnterOrSpace}
        >
          {tags.map((tag) => (
            <Box
              key={tag.id}
              bg={tag.color}
              color="white"
              fontFamily="instrumentSans"
              fontSize="12px"
              fontWeight="medium"
              h="20px"
              lineHeight="13px"
              px={1.75}
              _hover={{ filter: 'brightness(0.92)' }}
            >
              <Flex align="center" h="full">
                {tag.label}
              </Flex>
            </Box>
          ))}
          {tags.length === 0 && (
            <Text
              color="navigation.contextMuted"
              fontFamily="instrumentSans"
              fontSize="12px"
              lineHeight="20px"
            >
              {t('tags.noTagsAssigned')}
            </Text>
          )}
        </Flex>
      </Flex>

      <EditTagLinksModal
        currentTags={tags}
        entityId={article.id}
        entityType={EntityType.Article}
        isOpen={isOpen}
        onClose={onClose}
      />
    </>
  );
}

function ArticleTypeBadge({
  filled = false,
  icon,
  label,
}: {
  filled?: boolean;
  icon: React.ReactElement;
  label: string;
}) {
  return (
    <Flex
      align="center"
      bg={filled ? 'chakra-body-text' : 'transparent'}
      borderColor={filled ? 'chakra-body-text' : 'texas.burntSienna.500'}
      borderWidth="1px"
      color={filled ? 'chakra-body-bg' : 'texas.burntSienna.500'}
      fontFamily="barlow"
      fontSize="11px"
      fontWeight="bold"
      gap={1.25}
      h="22px"
      letterSpacing="0.08em"
      lineHeight="13px"
      px={2}
    >
      {icon}
      {label.toUpperCase()}
    </Flex>
  );
}
